import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { supabase } from '../lib/supabase';

interface EmailTestResult {
  system: 'SendGrid' | 'Nodemailer';
  success: boolean;
  messageId?: string;
  error?: string;
  timestamp: Date;
  deliveryTime?: number;
}

const EmailSystemTest: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<EmailTestResult[]>([]);
  const [testEmail] = useState('hapis2002raihan@gmail.com');

  const testSendGrid = async (): Promise<EmailTestResult> => {
    const startTime = Date.now();
    
    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: testEmail,
          subject: 'SubLime App - SendGrid Test Email',
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
              <div style="text-align: center; margin-bottom: 30px;">
                <h1 style="color: #32CD32; margin: 0;">SubLime</h1>
                <p style="color: #666; margin: 5px 0;">Subscription Management App</p>
              </div>
              
              <div style="background: #f9f9f9; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
                <h2 style="color: #333; margin-top: 0;">SendGrid Email Test</h2>
                <p style="color: #555; line-height: 1.6;">
                  This is a test email sent via <strong>SendGrid</strong> to verify email delivery performance.
                </p>
                <p style="color: #555; line-height: 1.6;">
                  <strong>Test Time:</strong> ${new Date().toLocaleString()}<br>
                  <strong>Purpose:</strong> Email system performance testing
                </p>
              </div>
              
              <div style="text-align: center; padding: 20px; background: #32CD32; color: white; border-radius: 8px;">
                <p style="margin: 0; font-weight: bold;">Email Delivered Successfully!</p>
                <p style="margin: 5px 0 0 0; font-size: 14px;">Check your inbox and spam folder</p>
              </div>
              
              <div style="text-align: center; margin-top: 20px; color: #999; font-size: 12px;">
                <p style="margin: 0;">This is a legitimate test email from SubLime App</p>
                <p style="margin: 5px 0 0 0;">If you received this in spam, please mark as "Not Spam"</p>
              </div>
            </div>
          `,
          text: `SubLime App - SendGrid Test Email

This is a test email sent via SendGrid to verify email delivery performance.

Test Time: ${new Date().toLocaleString()}
Purpose: Email system performance testing

Email Delivered Successfully!
Check your inbox and spam folder

This is a legitimate test email from SubLime App.
If you received this in spam, please mark as "Not Spam".`,
          type: 'test_sendgrid'
        }
      });

      if (error) throw error;

      const deliveryTime = Date.now() - startTime;
      
      return {
        system: 'SendGrid',
        success: true,
        messageId: data?.messageId || 'unknown',
        timestamp: new Date(),
        deliveryTime
      };
    } catch (error: unknown) {
      const deliveryTime = Date.now() - startTime;
      
      return {
        system: 'SendGrid',
        success: false,
        error: error instanceof Error ? (error as Error).message : 'Unknown error',
        timestamp: new Date(),
        deliveryTime
      };
    }
  };

  const testNodemailer = async (): Promise<EmailTestResult> => {
    const startTime = Date.now();
    
    try {
      // First, let's test if the function responds at all
      console.log('Testing Nodemailer function...');
      
      // Now using the real Nodemailer function
      const { data, error } = await supabase.functions.invoke('send-email-nodemailer', {
        body: {
          to: testEmail,
          subject: 'SubLime App - Nodemailer Test Email',
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
              <div style="text-align: center; margin-bottom: 30px;">
                <h1 style="color: #32CD32; margin: 0;">SubLime</h1>
                <p style="color: #666; margin: 5px 0;">Subscription Management App</p>
              </div>
              
              <div style="background: #f9f9f9; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
                <h2 style="color: #333; margin-top: 0;">Nodemailer Email Test</h2>
                <p style="color: #555; line-height: 1.6;">
                  This is a test email sent via <strong>Nodemailer</strong> using Gmail SMTP.
                </p>
                <p style="color: #555; line-height: 1.6;">
                  <strong>Test Time:</strong> ${new Date().toLocaleString()}<br>
                  <strong>Purpose:</strong> Email system performance testing<br>
                  <strong>System:</strong> Nodemailer with Gmail SMTP
                </p>
              </div>
              
              <div style="text-align: center; padding: 20px; background: #32CD32; color: white; border-radius: 8px;">
                <p style="margin: 0; font-weight: bold;">Email Delivered Successfully!</p>
                <p style="margin: 5px 0 0 0; font-size: 14px;">Check your inbox and spam folder</p>
              </div>
              
              <div style="text-align: center; margin-top: 20px; color: #999; font-size: 12px;">
                <p style="margin: 0;">This is a legitimate test email from SubLime App</p>
                <p style="margin: 5px 0 0 0;">If you received this in spam, please mark as "Not Spam"</p>
              </div>
            </div>
          `,
          text: `SubLime App - Nodemailer Test Email

This is a test email sent via Nodemailer using Gmail SMTP.

Test Time: ${new Date().toLocaleString()}
Purpose: Email system performance testing
System: Nodemailer with Gmail SMTP

Email Delivered Successfully!
Check your inbox and spam folder

This is a legitimate test email from SubLime App.
If you received this in spam, please mark as "Not Spam".`,
          type: 'test_nodemailer'
        }
      });

      console.log('Nodemailer response:', { data, error });

      if (error) throw error;

      const deliveryTime = Date.now() - startTime;
      
      return {
        system: 'Nodemailer',
        success: true,
        messageId: data?.messageId || 'unknown',
        timestamp: new Date(),
        deliveryTime
      };
    } catch (error: unknown) {
      console.error('Nodemailer error details:', error);
      const deliveryTime = Date.now() - startTime;
      
      return {
        system: 'Nodemailer',
        success: false,
        error: error instanceof Error ? (error as Error).message : 'Unknown error',
        timestamp: new Date(),
        deliveryTime
      };
    }
  };

  const testBothSystems = async () => {
    setIsLoading(true);
    setResults([]);

    try {
      // Test SendGrid first
      const sendGridResult = await testSendGrid();
      setResults(prev => [...prev, sendGridResult]);

      // Wait a bit between tests
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Test Nodemailer
      const nodemailerResult = await testNodemailer();
      setResults(prev => [...prev, nodemailerResult]);

    } catch (error: unknown) {
      console.error('Error testing email systems:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const testSingleSystem = async (system: 'SendGrid' | 'Nodemailer') => {
    setIsLoading(true);

    try {
      let result: EmailTestResult;
      
      if (system === 'SendGrid') {
        result = await testSendGrid();
      } else {
        result = await testNodemailer();
      }

      setResults(prev => [...prev, result]);
    } catch (error: unknown) {
      console.error(`Error testing ${system}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearResults = () => {
    setResults([]);
  };

  const getLatestResult = (system: 'SendGrid' | 'Nodemailer') => {
    return results
      .filter(r => r.system === system)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
  };

  const sendGridResult = getLatestResult('SendGrid');
  const nodemailerResult = getLatestResult('Nodemailer');

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        📧 Email System Comparison Test
      </Typography>
      
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Deployment Status:</strong><br/>
          • SendGrid: ✅ Deployed and working<br/>
          • Nodemailer: ✅ Deployed and working<br/>
          • Both systems are now live - test real performance differences!
        </Typography>
      </Alert>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Test both SendGrid and Nodemailer email systems to compare delivery speed and spam folder placement.
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🚀 SendGrid
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current email system using SendGrid API
              </Typography>
              
              <Button
                variant="outlined"
                startIcon={<Send />}
                onClick={() => testSingleSystem('SendGrid')}
                disabled={isLoading}
                fullWidth
                sx={{ mb: 2 }}
              >
                Test SendGrid
              </Button>

              {sendGridResult && (
                <Box>
                  <Chip 
                    label={sendGridResult.success ? 'Success' : 'Failed'}
                    color={sendGridResult.success ? 'success' : 'error'}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  {sendGridResult.success ? (
                    <Typography variant="body2">
                      Message ID: {sendGridResult.messageId}<br/>
                      Delivery Time: {sendGridResult.deliveryTime}ms
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="error">
                      Error: {sendGridResult.error}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ⚡ Nodemailer
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                New email system using Gmail SMTP
              </Typography>
              
              <Button
                variant="outlined"
                startIcon={<Send />}
                onClick={() => testSingleSystem('Nodemailer')}
                disabled={isLoading}
                fullWidth
                sx={{ mb: 2 }}
              >
                Test Nodemailer
              </Button>

              <Button
                variant="text"
                size="small"
                onClick={async () => {
                  try {
                    console.log('Testing Nodemailer function connection...');
                    const response = await fetch('https://cjwuilcrohkeykyzxdol.supabase.co/functions/v1/send-email-nodemailer', {
                      method: 'OPTIONS',
                      headers: {
                        'Content-Type': 'application/json',
                      }
                    });
                    console.log('Nodemailer function response:', response.status, response.statusText);
                    alert(`Function Status: ${response.status} ${response.statusText}`);
                  } catch (error: unknown) {
                    console.error('Function connection test failed:', error);
                    alert(`Connection failed: ${error instanceof Error ? (error as Error).message : 'Unknown error'}`);
                  }
                }}
                sx={{ mb: 1 }}
              >
                Debug Connection
              </Button>

              <Button
                variant="text"
                size="small"
                onClick={async () => {
                  try {
                    console.log('Testing Nodemailer function with minimal data...');
                    const response = await fetch('https://cjwuilcrohkeykyzxdol.supabase.co/functions/v1/send-email-nodemailer', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        to: 'test@example.com',
                        subject: 'Test',
                        html: '<p>Test</p>'
                      })
                    });
                    console.log('Minimal test response:', response.status, response.statusText);
                    const responseText = await response.text();
                    console.log('Response body:', responseText);
                    alert(`Minimal Test: ${response.status} ${response.statusText}\nBody: ${responseText}`);
                  } catch (error: unknown) {
                    console.error('Minimal test failed:', error);
                    alert(`Minimal test failed: ${error instanceof Error ? (error as Error).message : 'Unknown error'}`);
                  }
                }}
                sx={{ mb: 1 }}
              >
                Test Minimal Request
              </Button>

              {nodemailerResult && (
                <Box>
                  <Chip 
                    label={nodemailerResult.success ? 'Success' : 'Failed'}
                    color={nodemailerResult.success ? 'success' : 'error'}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  {nodemailerResult.success ? (
                    <Typography variant="body2">
                      Message ID: {nodemailerResult.messageId}<br/>
                      Delivery Time: {nodemailerResult.deliveryTime}ms
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="error">
                      Error: {nodemailerResult.error}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Send />}
          onClick={testBothSystems}
          disabled={isLoading}
          size="large"
          sx={{ mr: 2 }}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Test Both Systems'}
        </Button>

        <Button
          variant="outlined"
          onClick={clearResults}
          disabled={results.length === 0}
        >
          Clear Results
        </Button>
      </Box>

      {results.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📊 Test Results
            </Typography>
            
            {results.map((result, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Chip 
                    label={result.system}
                    color={result.system === 'SendGrid' ? 'primary' : 'secondary'}
                    size="small"
                  />
                  <Chip 
                    label={result.success ? 'Success' : 'Failed'}
                    color={result.success ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary">
                  {result.timestamp.toLocaleString()}
                </Typography>
                
                {result.success ? (
                  <Typography variant="body2">
                    Message ID: {result.messageId}<br/>
                    Delivery Time: {result.deliveryTime}ms
                  </Typography>
                ) : (
                  <Typography variant="body2" color="error">
                    Error: {result.error}
                  </Typography>
                )}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Testing Tips:</strong><br/>
          • Check both your inbox and spam folder for test emails<br/>
          • Compare delivery times between systems<br/>
          • Monitor spam folder placement rates<br/>
          • Test multiple times to get consistent results
        </Typography>
      </Alert>

      <Alert severity="success" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Improving Email Deliverability:</strong><br/>
          • <strong>Mark as "Not Spam"</strong> if emails go to spam folder<br/>
          • <strong>Add sender to contacts</strong> to improve future delivery<br/>
          • <strong>Reply to test emails</strong> to establish engagement<br/>
          • <strong>Use consistent sender address</strong> (app.gosublime@gmail.com)<br/>
          • <strong>Avoid spam trigger words</strong> in subject lines and content
        </Typography>
      </Alert>
    </Box>
  );
};

export default EmailSystemTest;
